/**
 * Componente: BillingTab
 * Descrição: Aba de Faturamento da tela de Gerenciamento do Tenant
 * 
 * Exibe:
 * - Card do plano atual com detalhes
 * - Próxima cobrança
 * - Histórico de faturas com download PDF
 * - Add-ons ativos
 * 
 * @author Sistema
 * @update 2026-01-19 - Criação inicial (Fase 2)
 */

import React, { useState } from "react";
import { 
  Crown, 
  Sparkles, 
  Zap, 
  Package, 
  CreditCard,
  Calendar,
  Clock,
  Download,
  ExternalLink,
  RefreshCw,
  Loader2,
  Plus,
  Check,
  Server,
  Eye,
  Users,
  Puzzle,
  MessageSquare,
  Inbox as InboxIcon,
  Receipt,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { TenantManagementData } from "@/hooks/useTenantManagement";
import { useNavigate } from "react-router-dom";
import { translateStripeDescription, translateInvoiceStatus } from "@/lib/stripe-description-translator";
import { AddonDetailsBillingSheet } from "./AddonDetailsBillingSheet";
import { PlanBillingSheet } from "./PlanBillingSheet";

// ============================================================================
// INTERFACES
// ============================================================================

interface BillingTabProps {
  data: TenantManagementData;
  billingHistory?: Array<{
    id: string;
    stripe_invoice_id: string | null;
    amount_paid: number;
    currency: string;
    status: string;
    invoice_url: string | null;
    invoice_pdf: string | null;
    plan_name: string | null;
    billing_period: string | null;
    period_start: string | null;
    period_end: string | null;
    created_at: string;
    line_items?: unknown[] | null;
  }>;
  tenantAddons?: Array<{
    id: string;
    name: string;
    addon_type?: string; // Tipo do add-on: inbox, agent, module, channel
    quantity: number;
    status: string;
    price_monthly?: number;
    provider_name?: string | null;
    provider_slug?: string | null;
    current_period_end?: string | null;
    stripe_price_id?: string | null; // NOVO: ID do preço no Stripe para vincular histórico
    linked_inboxes?: Array<{
      id: string;
      name: string;
      status: string;
      created_at: string;
      phone?: string | null;
    }>;
  }>;
  tenantId?: string; // NOVO: ID do tenant para buscar histórico de faturamento do add-on
  onManageBilling: () => void;
  onSync: () => void;
  isPortalLoading?: boolean;
  isSyncing?: boolean;
  isLoading?: boolean;
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

/**
 * Card do Plano Atual
 */
const CurrentPlanCard = ({
  planName,
  planId,
  billingPeriod,
  subscriptionStatus,
  currentPeriodEnd,
  isInTrial,
  trialDaysRemaining,
  onManageBilling,
  onSync,
  onOpenPlanBilling,
  isPortalLoading,
  isSyncing,
  isLoading,
}: {
  planName: string;
  planId: string;
  billingPeriod: string;
  subscriptionStatus: string;
  currentPeriodEnd: string | null;
  isInTrial: boolean;
  trialDaysRemaining: number;
  onManageBilling: () => void;
  onSync: () => void;
  onOpenPlanBilling: () => void;
  isPortalLoading?: boolean;
  isSyncing?: boolean;
  isLoading?: boolean;
}) => {
  // Ícone do plano
  const getPlanIcon = () => {
    switch (planId) {
      case "enterprise": return Crown;
      case "professional": return Sparkles;
      case "starter": return Zap;
      default: return Package;
    }
  };

  // Cor do plano
  const getPlanColor = () => {
    switch (planId) {
      case "enterprise": return "text-amber-600 dark:text-amber-400 bg-amber-500/10";
      case "professional": return "text-purple-600 dark:text-purple-400 bg-purple-500/10";
      case "starter": return "text-blue-600 dark:text-blue-400 bg-blue-500/10";
      default: return "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10";
    }
  };

  const Icon = getPlanIcon();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-14 w-14 rounded-xl" />
            <div className="flex-1">
              <Skeleton className="h-6 w-32 mb-2" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Informações do Plano */}
          <div className="flex items-center gap-4">
            <div className={cn(
              "flex h-14 w-14 items-center justify-center rounded-xl",
              getPlanColor()
            )}>
              <Icon className="h-7 w-7" />
            </div>
            
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-xl font-bold">{planName}</h3>
                {isInTrial ? (
                  <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-0">
                    Trial • {trialDaysRemaining} dias
                  </Badge>
                ) : subscriptionStatus === "active" ? (
                  <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-0">
                    <Check className="h-3 w-3 mr-1" />
                    Ativo
                  </Badge>
                ) : subscriptionStatus === "past_due" ? (
                  <Badge variant="destructive">
                    Pagamento Pendente
                  </Badge>
                ) : null}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Período: {billingPeriod === "yearly" ? "Anual" : "Mensal"}
              </p>
            </div>
          </div>

          {/* Próxima Cobrança e Ações */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {currentPeriodEnd && subscriptionStatus === "active" && (
              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Próxima cobrança</p>
                  <p className="font-semibold">
                    {format(new Date(currentPeriodEnd), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={onSync}
                disabled={isSyncing}
                title="Sincronizar com Stripe"
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="outline"
                onClick={onManageBilling}
                disabled={isPortalLoading}
              >
                {isPortalLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                Gerenciar Pagamento
              </Button>
              <Button
                variant="outline"
                onClick={onOpenPlanBilling}
                title="Ver histórico de pagamentos do plano"
              >
                <Receipt className="h-4 w-4 mr-2" />
                Faturamento
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Formatar status da fatura
 */
const getInvoiceStatusBadge = (status: string) => {
  const translatedStatus = translateInvoiceStatus(status, "pt-BR");
  
  switch (status) {
    case "paid":
      return (
        <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-0">
          {translatedStatus}
        </Badge>
      );
    case "open":
    case "pending":
      return (
        <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-0">
          {translatedStatus}
        </Badge>
      );
    case "failed":
    case "uncollectible":
      return (
        <Badge variant="destructive">
          {translatedStatus}
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary">
          {translatedStatus}
        </Badge>
      );
  }
};

/**
 * Formatar valor em centavos para reais
 */
const formatCurrency = (valueInCents: number, currency: string = "brl") => {
  const value = valueInCents / 100;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(value);
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function BillingTab({
  data,
  billingHistory = [],
  tenantAddons = [],
  tenantId, // NOVO: ID do tenant
  onManageBilling,
  onSync,
  isPortalLoading,
  isSyncing,
  isLoading,
}: BillingTabProps) {
  const navigate = useNavigate();
  
  // Estado para controlar o sheet de detalhes do add-on
  const [selectedAddon, setSelectedAddon] = useState<typeof tenantAddons[0] | null>(null);
  const [isAddonSheetOpen, setIsAddonSheetOpen] = useState(false);
  
  // Estado para controlar o sheet de faturamento do plano
  const [isPlanBillingSheetOpen, setIsPlanBillingSheetOpen] = useState(false);

  // Calcular total mensal dos add-ons
  const totalAddonsMonthly = tenantAddons.reduce(
    (sum, addon) => sum + (addon.price_monthly || 0) * addon.quantity,
    0
  );

  // Handler para abrir detalhes do add-on
  const handleViewAddonDetails = (addon: typeof tenantAddons[0]) => {
    setSelectedAddon(addon);
    setIsAddonSheetOpen(true);
  };

  return (
    <div className="space-y-8">
      {/* Card do Plano Atual */}
      <CurrentPlanCard
        planName={data.billing.planName}
        planId={data.billing.plan}
        billingPeriod={data.billing.billingPeriod}
        subscriptionStatus={data.billing.subscriptionStatus}
        currentPeriodEnd={data.billing.currentPeriodEnd}
        isInTrial={data.billing.isInTrial}
        trialDaysRemaining={data.billing.trialDaysRemaining}
        onManageBilling={onManageBilling}
        onSync={onSync}
        onOpenPlanBilling={() => setIsPlanBillingSheetOpen(true)}
        isPortalLoading={isPortalLoading}
        isSyncing={isSyncing}
        isLoading={isLoading}
      />

      {/* Seção de Add-ons Ativos - Layout expandido */}
      {tenantAddons.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5 text-primary" />
                  Add-ons Ativos
                </CardTitle>
                <CardDescription>
                  {tenantAddons.length} add-on{tenantAddons.length !== 1 ? "s" : ""} contratado{tenantAddons.length !== 1 ? "s" : ""}
                </CardDescription>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Total mensal</p>
                <p className="text-lg font-semibold text-primary">
                  {formatCurrency(totalAddonsMonthly * 100)}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {tenantAddons.map((addon) => {
                const linkedCount = addon.linked_inboxes?.length || 0;
                const unitPrice = addon.price_monthly || 0;
                const subtotal = unitPrice * addon.quantity;
                
                return (
                  <div
                    key={addon.id}
                    className="p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                  >
                    {/* Linha principal */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Package className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{addon.name}</p>
                            <Badge
                              variant="secondary"
                              className={cn(
                                addon.status === "active" && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                                addon.status === "pending" && "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                              )}
                            >
                              {addon.status === "active" ? "Ativo" : addon.status === "pending" ? "Pendente" : addon.status}
                            </Badge>
                          </div>
                          
                          {/* Informações secundárias */}
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                            {addon.provider_name && (
                              <span className="flex items-center gap-1">
                                <Server className="h-3 w-3" />
                                {addon.provider_name}
                              </span>
                            )}
                            <span>Qtd: {addon.quantity}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Preço */}
                      <div className="text-right flex-shrink-0">
                        <p className="font-semibold text-primary">
                          {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(subtotal)}/mês
                        </p>
                        {addon.quantity > 1 && (
                          <p className="text-xs text-muted-foreground">
                            {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(unitPrice)} x {addon.quantity}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Linha de rodapé com informações extras - condicional por tipo */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        {/* Exibição condicional baseada no tipo do add-on */}
                        {addon.addon_type === 'inbox' && (
                          <span className="flex items-center gap-1">
                            <InboxIcon className="h-3 w-3" />
                            Inboxes: {linkedCount}/{addon.quantity}
                          </span>
                        )}
                        {addon.addon_type === 'agent' && (
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            Agentes contratados: {addon.quantity}
                          </span>
                        )}
                        {addon.addon_type === 'module' && (
                          <span className="flex items-center gap-1">
                            <Puzzle className="h-3 w-3" />
                            Módulo ativo
                          </span>
                        )}
                        {addon.addon_type === 'channel' && (
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            Canais: {addon.quantity}
                          </span>
                        )}
                        {/* Fallback para tipos não mapeados */}
                        {!addon.addon_type && (
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            Qtd: {addon.quantity}
                          </span>
                        )}
                        {addon.current_period_end && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Renovação: {format(new Date(addon.current_period_end), "dd/MM/yyyy", { locale: ptBR })}
                          </span>
                        )}
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => handleViewAddonDetails(addon)}
                      >
                        <Eye className="h-3 w-3" />
                        Ver Detalhes
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sheet de detalhes do add-on com histórico de faturamento */}
      <AddonDetailsBillingSheet
        open={isAddonSheetOpen}
        onOpenChange={setIsAddonSheetOpen}
        addon={selectedAddon}
        tenantId={tenantId}
        billingHistory={billingHistory}
      />

      {/* Sheet de faturamento do plano */}
      <PlanBillingSheet
        open={isPlanBillingSheetOpen}
        onOpenChange={setIsPlanBillingSheetOpen}
        planName={data.billing.planName}
        planId={data.billing.plan}
        billingHistory={billingHistory}
      />

      {/* Botão para comprar add-ons */}
      <div className="flex justify-center">
        <Button
          variant="outline"
          onClick={() => navigate("/tenant/plans#addons")}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Ver Add-ons Disponíveis
        </Button>
      </div>

      <Separator />

      {/* Histórico de Faturas */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Histórico de Faturas</h3>
          <p className="text-sm text-muted-foreground">
            Visualize e baixe suas faturas anteriores
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : billingHistory.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Nenhuma fatura encontrada
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-xl border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Data</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center w-[100px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {billingHistory.slice(0, 10).map((invoice) => {
                  // Traduzir descrição
                  const description = invoice.plan_name
                    ? translateStripeDescription(invoice.plan_name, "pt-BR")
                    : "Fatura";
                  
                  // Verificar se há itens adicionais
                  const hasMultipleItems = invoice.line_items && invoice.line_items.length > 1;

                  return (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">
                        {format(new Date(invoice.created_at), "dd/MM/yyyy", { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{description}</span>
                          {hasMultipleItems && (
                            <Badge variant="secondary" className="text-xs">
                              +{(invoice.line_items?.length || 1) - 1} item
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(invoice.amount_paid, invoice.currency)}
                      </TableCell>
                      <TableCell className="text-center">
                        {getInvoiceStatusBadge(invoice.status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          {invoice.invoice_pdf && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => window.open(invoice.invoice_pdf!, "_blank")}
                              title="Baixar PDF"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          {invoice.invoice_url && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => window.open(invoice.invoice_url!, "_blank")}
                              title="Ver no Stripe"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {billingHistory.length > 10 && (
          <div className="text-center">
            <Button variant="link" onClick={() => navigate("/tenant/plans")}>
              Ver todas as faturas →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
